import { type FC, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectUserRoles } from '../redux/reducers/UserRolesSlice'
import {
  ADMINISTRATION_B2B_PARTNER_ENDPOINT,
  ADMINISTRATION_BED_MANAGEMENT_ENDPOINT,
  ADMINISTRATION_ENDPOINT,
  ADMINISTRATION_PATHOLOGIST_ENDPOINT,
  ADMINISTRATION_PROCESSING_UNIT_ENDPOINT,
  ADMINISTRATION_RBAC_ENDPOINT,
  ADMINISTRATION_USER_MANAGEMENT_ENDPOINT,
  ADMIN_ROLE_ADMINISTRATION,
  ADMIN_ROLE_BED_MANAGEMENT,
  ADMIN_ROLE_DASHBOARD_PAGE,
  ADMIN_ROLE_PATHOLOGIST,
  ADMIN_ROLE_ROLE_MANAGEMENT,
  ADMIN_ROLE_USER_MANAGEMENT,
  DASHBOARD_ENDPOINT,
  INDENT_ROLE_PATIENT_INDENT,
  INDENT_ROLE_PATIENT_INDENT_LIST,
  INDENT_ROLE_PATIENT_INDENT_RAISE,
  INDENT_ROLE_PATIENT_INDENT_RETURN,
  IPD_BILLING_ENDPOINT,
  IPD_ENDPOINT,
  IPD_LAB_ORDER_ENDPOINT,
  IPD_REGISTRATION_ENDPOINT,
  IPD_ROLE_IPD_BILLING,
  IPD_ROLE_IPD_LAB_ORDER,
  IPD_ROLE_IPD_MAIN,
  IPD_ROLE_IPD_REGISTRATION,
  IPD_ROLE_IPD_VISITS,
  IPD_VISITS_ENDPOINT,
  LAB_ENDPOINT,
  LAB_MANAGEMENT_ADMIN_ROLE,
  LAB_MANAGEMENT_DEPARTMENTS_ENDPOINT,
  LAB_MANAGEMENT_ENDPOINT,
  LAB_MANAGEMENT_LAB_DEPARTMENT_ROLE,
  LAB_MANAGEMENT_LAB_TESTS_ROLE,
  LAB_MANAGEMENT_METHOD_TYPES_ENDPOINT,
  LAB_MANAGEMENT_METHOD_TYPES_ROLE,
  LAB_MANAGEMENT_PARAMETERS_ENDPOINT,
  LAB_MANAGEMENT_PARAMETERS_ROLE,
  LAB_MANAGEMENT_PARAMETERS_SEQUENCES_ROLE,
  LAB_MANAGEMENT_PARAMETER_RANGES_ENDPOINT,
  LAB_MANAGEMENT_PARAMETER_RANGES_ROLE,
  LAB_MANAGEMENT_PARAMETER_SEQUENCES_ENDPOINT,
  LAB_MANAGEMENT_RESULT_TYPES_ENDPOINT,
  LAB_MANAGEMENT_RESULT_TYPES_ROLE,
  LAB_MANAGEMENT_SAMPLE_TYPES_ENDPOINT,
  LAB_MANAGEMENT_SAMPLE_TYPES_ROLE,
  LAB_MANAGEMENT_TESTS_ENDPOINT,
  LAB_MANAGEMENT_UNIT_TYPES_ENDPOINT,
  LAB_MANAGEMENT_UNIT_TYPES_ROLE,
  LAB_ORDERS_PROCESSING,
  LAB_ROLE_LAB_ORDERS_MAIN,
  LAB_ROLE_LAB_ORDER_PROCESSING_PAGE,
  OPD_BILLING_ENDPOINT,
  OPD_EMERGENCY_ENDPOINT,
  OPD_ENDPOINT,
  OPD_LAB_ORDER_ENDPOINT,
  OPD_REGISTRATION_ENDPOINT,
  OPD_ROLE_OPD_BILLING,
  OPD_ROLE_OPD_EMERGENCY,
  OPD_ROLE_OPD_LAB_ORDER,
  OPD_ROLE_OPD_MAIN,
  OPD_ROLE_OPD_REGISTRATION,
  OPD_ROLE_OPD_VISITS,
  OPD_VISITS_ENDPOINT,
  PATIENT_INDENT_ENDPOINT,
  PATIENT_INDENT_LIST_ENDPOINT,
  PATIENT_INDENT_RAISE_ENDPOINT,
  PATIENT_INDENT_RETURN_ENDPOINT,
  PROFILE_ENDPOINT,
  PROFILE_ROLE_PROFILE_MAIN,
  LAB_REFERRED_OUT,
  LAB_ROLE_LAB_ORDERS_REFFERED_OUT_PAGE,
  WARDS_ENDPOINT,
  ROOMS_ENDPOINT,
  BEDS_ENDPOINT,
  SETTINGS_ENDPOINT,
  SETTINGS_ROLE_BEDS,
  SETTINGS_ROLE_ROOMS,
  SETTINGS_ROLE_WARDS,
} from '../utils/Endpoints'
import { lazyWithRetry } from '../utils/lazyWithRetry'

export interface Route {
  key: string
  title: string
  path: string
  enabled?: boolean
  component: FC
  index: number
  isMenuItem: boolean
  icon?: FC<object>
  children?: Array<Route>
}

const Opd = lazyWithRetry(() => import('../pages/opd/Opd'))
const OpdRegistration = lazyWithRetry(
  () => import('../pages/registration/RegistrationForm')
)
// const OpdAppointment = lazyWithRetry(
//   () => import('../pages/opd/appointments/Appointment')
// )
const OpdBilling = lazyWithRetry(() => import('../pages/opd/billing/Billing'))
const OpdVisits = lazyWithRetry(() => import('../pages/opd/visits/Visits'))
const OpdOrder = lazyWithRetry(() => import('../pages/opd/labOrder/LabOrder'))
const OpdEmergency = lazyWithRetry(() => import('../pages/emergency/Emergency'))

const Ipd = lazyWithRetry(() => import('../pages/ipd/Ipd'))
const IpdRegistration = lazyWithRetry(
  () => import('../pages/registration/RegistrationForm')
)
const IpdBilling = lazyWithRetry(() => import('../pages/ipd/billing/Billing'))
const IpdVisits = lazyWithRetry(() => import('../pages/ipd/visits/Visits'))

const Dashboard = lazyWithRetry(() => import('../pages/dashboard/Dashboard'))

const Lab = lazyWithRetry(() => import('../pages/lab/Lab'))
const LabOrderProcessing = lazyWithRetry(
  () => import('../pages/lab/LabOrderProcessing')
)
const ReferredOut = lazyWithRetry(
  () => import('../pages/lab/referredOut/ReferredOut')
)

const LabManagement = lazyWithRetry(
  () => import('../pages/labManagement/LabManagement')
)
const LabDepartments = lazyWithRetry(
  () => import('../pages/labManagement/labDepartments/LabDepartments')
)

const Administration = lazyWithRetry(
  () => import('../pages/administration/Administration')
)
const Pathologist = lazyWithRetry(
  () => import('../pages/administration/pathologist/Pathologist')
)
const B2BPartner = lazyWithRetry(
  () => import('../pages/administration/b2bPartner/B2BPartner')
)
const ProcessingUnit = lazyWithRetry(
  () => import('../pages/administration/processingUnit/ProcessingUnit')
)
const RoleManagement = lazyWithRetry(
  () => import('../pages/administration/rbac/RoleList')
)
const BedManagement = lazyWithRetry(
  () => import('../pages/bedManagement/BedManagement')
)
const Wards = lazyWithRetry(() => import('../pages/settings/wards/Wards'))
const Rooms = lazyWithRetry(() => import('../pages/settings/rooms/Rooms'))
const Beds = lazyWithRetry(() => import('../pages/settings/beds/Beds'))
const Settings = lazyWithRetry(() => import('../pages/settings/Settings'))
const SettingsLandingPage = lazyWithRetry(
  () => import('../pages/settings/SettingsLandingPage')
)
const UserManagement = lazyWithRetry(
  () => import('../pages/administration/userManagement/UserList')
)
const LabTests = lazyWithRetry(
  () => import('../pages/labManagement/labTests/LabTests')
)
const Parameters = lazyWithRetry(
  () => import('../pages/labManagement/parameters/Parameters')
)
const ParameterRanges = lazyWithRetry(
  () => import('../pages/labManagement/parameterRanges/ParameterRanges')
)
const ParameterSequences = lazyWithRetry(
  () => import('../pages/labManagement/parameterSequences/ParameterSequences')
)
const SampleTypes = lazyWithRetry(
  () => import('../pages/labManagement/sampleTypes/SampleTypes')
)
const ResultTypes = lazyWithRetry(
  () => import('../pages/labManagement/resultTypes/ResultTypes')
)
const UnitTypes = lazyWithRetry(
  () => import('../pages/labManagement/unitTypes/UnitTypes')
)
const MethodTypes = lazyWithRetry(
  () => import('../pages/labManagement/methodTypes/MethodTypes')
)

const ProfilePage = lazyWithRetry(() => import('../pages/profile/Profile'))

const PatientIndent = lazyWithRetry(
  () => import('../pages/patientIndent/PatientIndent')
)
const PatientIndentList = lazyWithRetry(
  () => import('../pages/patientIndent/IndentList')
)
const PatientIndentRaise = lazyWithRetry(
  () => import('../pages/patientIndent/RaiseIndent')
)
const PatientIndentReturn = lazyWithRetry(
  () => import('../pages/patientIndent/RaiseReturnIndent')
)

// 🔑 Hook that returns routes enabled/disabled depending on Redux roles
export const useRoutes = (): Array<Route> => {
  const roles = useSelector(selectUserRoles)
  return useMemo(() => {
    const hasRole = (role: string) => roles?.includes(role)

    return [
      {
        key: 'dashboard',
        title: 'Dashboard',
        path: DASHBOARD_ENDPOINT,
        enabled: hasRole(ADMIN_ROLE_DASHBOARD_PAGE),
        component: Dashboard,
        index: 1,
        isMenuItem: true,
        children: [],
      },
      {
        key: 'opd',
        title: 'OPD',
        path: OPD_ENDPOINT,
        enabled: hasRole(OPD_ROLE_OPD_MAIN),
        component: Opd,
        index: 2,
        isMenuItem: true,
        children: [
          {
            key: 'registration',
            title: 'Registration',
            path: OPD_REGISTRATION_ENDPOINT,
            enabled: hasRole(OPD_ROLE_OPD_REGISTRATION),
            component: OpdRegistration,
            index: 1,
            isMenuItem: false,
          },
          {
            key: 'visits',
            title: 'Visits',
            path: OPD_VISITS_ENDPOINT,
            enabled: hasRole(OPD_ROLE_OPD_VISITS),
            component: OpdVisits,
            index: 2,
            isMenuItem: false,
          },
          // {
          //   key: 'appointments',
          //   title: 'Appointments',
          //   path: OPD_APPOINTMENTS_ENDPOINT,
          //   enabled: hasRole(OPD_ROLE_OPD_APPOINTMENTS),
          //   component: OpdAppointment,
          //   index: 3,
          //   isMenuItem: false,
          // },
          {
            key: 'billing',
            title: 'Billing',
            path: OPD_BILLING_ENDPOINT,
            enabled: hasRole(OPD_ROLE_OPD_BILLING),
            component: OpdBilling,
            index: 3,
            isMenuItem: false,
          },
          {
            key: 'labOrder',
            title: 'Lab Orders',
            path: OPD_LAB_ORDER_ENDPOINT,
            enabled: hasRole(OPD_ROLE_OPD_LAB_ORDER),
            component: OpdOrder,
            index: 4,
            isMenuItem: false,
          },
          {
            key: 'emergency',
            title: 'Emergency',
            path: OPD_EMERGENCY_ENDPOINT,
            enabled: hasRole(OPD_ROLE_OPD_EMERGENCY),
            component: OpdEmergency,
            index: 5,
            isMenuItem: false,
          },
        ],
      },
      {
        key: 'ipd',
        title: 'IPD',
        path: IPD_ENDPOINT,
        enabled: hasRole(IPD_ROLE_IPD_MAIN),
        component: Ipd,
        index: 3,
        isMenuItem: true,
        children: [
          {
            key: 'registration',
            title: 'Registration',
            path: IPD_REGISTRATION_ENDPOINT,
            enabled: hasRole(IPD_ROLE_IPD_REGISTRATION),
            component: IpdRegistration,
            index: 1,
            isMenuItem: false,
          },
          {
            key: 'visits',
            title: 'Visits',
            path: IPD_VISITS_ENDPOINT,
            enabled: hasRole(IPD_ROLE_IPD_VISITS),
            component: IpdVisits,
            index: 2,
            isMenuItem: false,
          },
          {
            key: 'billing',
            title: 'Billing',
            path: IPD_BILLING_ENDPOINT,
            enabled: hasRole(IPD_ROLE_IPD_BILLING),
            component: IpdBilling,
            index: 3,
            isMenuItem: false,
          },
          {
            key: 'labOrder',
            title: 'Lab Orders',
            path: IPD_LAB_ORDER_ENDPOINT,
            enabled: hasRole(IPD_ROLE_IPD_LAB_ORDER),
            component: OpdOrder,
            index: 4,
            isMenuItem: false,
          },
        ],
      },
      {
        key: 'lab',
        title: 'LAB ORDERS',
        path: LAB_ENDPOINT,
        enabled: hasRole(LAB_ROLE_LAB_ORDERS_MAIN),
        component: Lab,
        index: 4,
        isMenuItem: false,
        children: [
          {
            key: 'lab-order-processing',
            title: 'Lab Order Processing',
            path: `${LAB_ORDERS_PROCESSING}/*`,
            enabled: hasRole(LAB_ROLE_LAB_ORDER_PROCESSING_PAGE),
            component: LabOrderProcessing,
            index: 1,
            isMenuItem: false,
          },
          {
            key: 'referred-out',
            title: 'Referred Out',
            path: `${LAB_REFERRED_OUT}/*`,
            enabled: hasRole(LAB_ROLE_LAB_ORDERS_REFFERED_OUT_PAGE),
            component: ReferredOut,
            index: 2,
            isMenuItem: false,
          },
        ],
      },
      {
        key: 'labManagement',
        title: 'LAB MANAGEMENT',
        path: LAB_MANAGEMENT_ENDPOINT,
        enabled: hasRole(LAB_MANAGEMENT_ADMIN_ROLE),
        component: LabManagement,
        index: 5,
        isMenuItem: false,
        children: [
          {
            key: 'lab-departments',
            title: 'Lab Departments',
            path: LAB_MANAGEMENT_DEPARTMENTS_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_LAB_DEPARTMENT_ROLE),
            component: LabDepartments,
            index: 1,
            isMenuItem: false,
          },
          {
            key: 'lab-tests',
            title: 'Lab Tests',
            path: LAB_MANAGEMENT_TESTS_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_LAB_TESTS_ROLE),
            component: LabTests,
            index: 2,
            isMenuItem: false,
          },
          {
            key: 'parameters',
            title: 'Parameters',
            path: LAB_MANAGEMENT_PARAMETERS_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_PARAMETERS_ROLE),
            component: Parameters,
            index: 3,
            isMenuItem: false,
          },
          {
            key: 'parameter-ranges',
            title: 'Parameter Ranges',
            path: LAB_MANAGEMENT_PARAMETER_RANGES_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_PARAMETER_RANGES_ROLE),
            component: ParameterRanges,
            index: 4,
            isMenuItem: false,
          },
          {
            key: 'parameter-sequences',
            title: 'Parameter Sequences',
            path: LAB_MANAGEMENT_PARAMETER_SEQUENCES_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_PARAMETERS_SEQUENCES_ROLE),
            component: ParameterSequences,
            index: 5,
            isMenuItem: false,
          },
          {
            key: 'sample-types',
            title: 'Sample Types',
            path: LAB_MANAGEMENT_SAMPLE_TYPES_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_SAMPLE_TYPES_ROLE),
            component: SampleTypes,
            index: 6,
            isMenuItem: false,
          },
          {
            key: 'result-types',
            title: 'Result Types',
            path: LAB_MANAGEMENT_RESULT_TYPES_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_RESULT_TYPES_ROLE),
            component: ResultTypes,
            index: 7,
            isMenuItem: false,
          },
          {
            key: 'unit-types',
            title: 'Unit Types',
            path: LAB_MANAGEMENT_UNIT_TYPES_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_UNIT_TYPES_ROLE),
            component: UnitTypes,
            index: 8,
            isMenuItem: false,
          },
          {
            key: 'method-types',
            title: 'Method Types',
            path: LAB_MANAGEMENT_METHOD_TYPES_ENDPOINT,
            enabled: hasRole(LAB_MANAGEMENT_METHOD_TYPES_ROLE),
            component: MethodTypes,
            index: 9,
            isMenuItem: false,
          },
        ],
      },
      {
        key: 'administration',
        title: 'ADMINISTRATION',
        path: ADMINISTRATION_ENDPOINT,
        enabled: hasRole(ADMIN_ROLE_ADMINISTRATION),
        component: Administration,
        index: 6,
        isMenuItem: false,
        children: [
          {
            key: 'pathologist',
            title: 'Pathologist',
            path: ADMINISTRATION_PATHOLOGIST_ENDPOINT,
            enabled: hasRole(ADMIN_ROLE_PATHOLOGIST),
            component: Pathologist,
            index: 1,
            isMenuItem: false,
          },
          {
            key: 'b2bPartner',
            title: 'B2B Partners',
            path: ADMINISTRATION_B2B_PARTNER_ENDPOINT,
            enabled: false,
            component: B2BPartner,
            index: 2,
            isMenuItem: false,
          },
          {
            key: 'processingUnit',
            title: 'Processing Unit',
            path: ADMINISTRATION_PROCESSING_UNIT_ENDPOINT,
            enabled: false,
            component: ProcessingUnit,
            index: 3,
            isMenuItem: false,
          },
          {
            key: 'bedManagement',
            title: 'Bed Management',
            path: ADMINISTRATION_BED_MANAGEMENT_ENDPOINT,
            enabled: hasRole(ADMIN_ROLE_BED_MANAGEMENT),
            component: BedManagement,
            index: 4,
            isMenuItem: false,
          },
          {
            key: 'rbac',
            title: 'Role Management',
            path: ADMINISTRATION_RBAC_ENDPOINT,
            enabled: hasRole(ADMIN_ROLE_ROLE_MANAGEMENT),
            component: RoleManagement,
            index: 5,
            isMenuItem: false,
          },
          {
            key: 'userManagement',
            title: 'User Management',
            path: ADMINISTRATION_USER_MANAGEMENT_ENDPOINT,
            enabled: hasRole(ADMIN_ROLE_USER_MANAGEMENT),
            component: UserManagement,
            index: 6,
            isMenuItem: false,
          },
        ],
      },
      {
        key: 'patientIndent',
        title: 'Patient Indent',
        path: PATIENT_INDENT_ENDPOINT,
        enabled: hasRole(INDENT_ROLE_PATIENT_INDENT),
        component: PatientIndent,
        index: 7,
        isMenuItem: true,
        children: [
          {
            key: 'list',
            title: 'List',
            path: PATIENT_INDENT_LIST_ENDPOINT,
            enabled: hasRole(INDENT_ROLE_PATIENT_INDENT_LIST),
            component: PatientIndentList,
            index: 1,
            isMenuItem: false,
          },
          {
            key: 'raise',
            title: 'Raise',
            path: PATIENT_INDENT_RAISE_ENDPOINT,
            enabled: hasRole(INDENT_ROLE_PATIENT_INDENT_RAISE),
            component: PatientIndentRaise,
            index: 2,
            isMenuItem: false,
          },
          {
            key: 'return',
            title: 'Return',
            path: PATIENT_INDENT_RETURN_ENDPOINT,
            enabled: hasRole(INDENT_ROLE_PATIENT_INDENT_RETURN),
            component: PatientIndentReturn,
            index: 3,
            isMenuItem: false,
          },
        ],
      },

      {
        key: 'profile',
        title: 'Profile',
        path: PROFILE_ENDPOINT,
        enabled: hasRole(PROFILE_ROLE_PROFILE_MAIN),
        component: ProfilePage,
        index: 8,
        isMenuItem: false,
      },
      {
        key: 'settings',
        title: 'Settings',
        path: SETTINGS_ENDPOINT,
        enabled: true,
        component: Settings,
        index: 11,
        isMenuItem: false,
        children: [
          {
            key: 'settings-landing',
            title: 'Settings Landing Page',
            path: '',
            enabled: true,
            component: SettingsLandingPage,
            index: 0,
            isMenuItem: false,
          },
          {
            key: 'wards',
            title: 'Wards',
            path: WARDS_ENDPOINT,
            enabled: hasRole(SETTINGS_ROLE_WARDS),
            component: Wards,
            index: 1,
            isMenuItem: false,
          },
          {
            key: 'rooms',
            title: 'Rooms',
            path: ROOMS_ENDPOINT,
            enabled: hasRole(SETTINGS_ROLE_ROOMS),
            component: Rooms,
            index: 2,
            isMenuItem: false,
          },
          {
            key: 'beds',
            title: 'Beds',
            path: BEDS_ENDPOINT,
            enabled: hasRole(SETTINGS_ROLE_BEDS),
            component: Beds,
            index: 3,
            isMenuItem: false,
          },
        ],
      },
    ]
  }, [roles])
}
